import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Search, UserPlus } from 'lucide-react'
import { type FormEvent, useState } from 'react'
import { ClientDetailDrawer } from '../components/ClientDetailDrawer'
import { AppLayout, PageHeader } from '../components/layout/AppLayout'
import { Badge, clientStatusVariant } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { Card } from '../components/ui/Card'
import { Dialog } from '../components/ui/Dialog'
import { Empty, Skeleton } from '../components/ui/Empty'
import { Input, Label } from '../components/ui/Input'
import { apiClient } from '../lib/api'
import { formatDate } from '../lib/utils'

type Client = {
  id: string
  name: string
  legalName: string | null
  status: string
  vatNumber: string | null
  city: string | null
  email: string | null
  updatedAt: string
}

type ListResult = { results: Client[]; total: number; limit: number; offset: number }

export function ClientsPage() {
  const qc = useQueryClient()
  const [search, setSearch] = useState('')
  const [dialogOpen, setDialogOpen] = useState(false)
  const [openClientId, setOpenClientId] = useState<string | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['clients', { search }],
    queryFn: () =>
      apiClient.get<ListResult>(`/clients${search ? `?search=${encodeURIComponent(search)}` : ''}`),
  })

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) => apiClient.post('/clients', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clients'] })
      setDialogOpen(false)
    },
  })

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const fd = new FormData(e.currentTarget)
    const body = Object.fromEntries(fd.entries())
    create.mutate(body)
  }

  return (
    <AppLayout>
      <PageHeader
        title="Clienti"
        description={`${data?.total ?? 0} clienti totali`}
        action={
          <Button onClick={() => setDialogOpen(true)}>
            <Plus className="size-4" /> Nuovo cliente
          </Button>
        }
      />

      <div className="mb-4 relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-zinc-500 pointer-events-none" />
        <Input
          placeholder="Cerca per nome o P.IVA…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900/60 text-zinc-400 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-5 py-3 font-medium">Nome</th>
              <th className="text-left px-5 py-3 font-medium">P.IVA</th>
              <th className="text-left px-5 py-3 font-medium">Città</th>
              <th className="text-left px-5 py-3 font-medium">Stato</th>
              <th className="text-left px-5 py-3 font-medium">Aggiornato</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 6 }).map((_, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton placeholder
                <tr key={i} className="border-t border-zinc-800">
                  <td className="px-5 py-3">
                    <Skeleton className="h-4 w-40" />
                  </td>
                  <td className="px-5 py-3">
                    <Skeleton className="h-4 w-28" />
                  </td>
                  <td className="px-5 py-3">
                    <Skeleton className="h-4 w-24" />
                  </td>
                  <td className="px-5 py-3">
                    <Skeleton className="h-5 w-16" />
                  </td>
                  <td className="px-5 py-3">
                    <Skeleton className="h-4 w-20" />
                  </td>
                </tr>
              ))
            ) : data && data.results.length > 0 ? (
              data.results.map((client) => (
                <tr
                  key={client.id}
                  onClick={() => setOpenClientId(client.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') setOpenClientId(client.id)
                  }}
                  className="border-t border-zinc-800 hover:bg-zinc-900/40 cursor-pointer transition"
                >
                  <td className="px-5 py-3">
                    <div className="font-medium text-zinc-100">{client.name}</div>
                    {client.legalName && client.legalName !== client.name && (
                      <div className="text-xs text-zinc-500">{client.legalName}</div>
                    )}
                  </td>
                  <td className="px-5 py-3 text-zinc-300 tabular-nums">
                    {client.vatNumber ?? '—'}
                  </td>
                  <td className="px-5 py-3 text-zinc-300">{client.city ?? '—'}</td>
                  <td className="px-5 py-3">
                    <Badge variant={clientStatusVariant(client.status)}>{client.status}</Badge>
                  </td>
                  <td className="px-5 py-3 text-zinc-500 text-xs">
                    {formatDate(client.updatedAt)}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5}>
                  <Empty
                    icon={UserPlus}
                    title="Nessun cliente"
                    description="Crea il primo cliente o importa la lista dal tuo CRM precedente."
                    action={
                      <Button onClick={() => setDialogOpen(true)}>
                        <Plus className="size-4" /> Nuovo cliente
                      </Button>
                    }
                  />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title="Nuovo cliente"
        description="Crea un nuovo cliente. Solo Nome è obbligatorio."
      >
        <form onSubmit={onSubmit} className="p-5 space-y-4">
          <div>
            <Label htmlFor="name">Nome *</Label>
            <Input id="name" name="name" required placeholder="ACME Srl" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="vatNumber">P.IVA</Label>
              <Input id="vatNumber" name="vatNumber" placeholder="IT12345678901" />
            </div>
            <div>
              <Label htmlFor="fiscalCode">Codice fiscale</Label>
              <Input id="fiscalCode" name="fiscalCode" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" name="email" type="email" />
            </div>
            <div>
              <Label htmlFor="phone">Telefono</Label>
              <Input id="phone" name="phone" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Label htmlFor="city">Città</Label>
              <Input id="city" name="city" />
            </div>
            <div>
              <Label htmlFor="province">Prov.</Label>
              <Input id="province" name="province" maxLength={2} />
            </div>
          </div>
          {create.isError && (
            <div className="text-sm text-red-400">
              {(create.error as Error)?.message ?? 'Errore creazione'}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)}>
              Annulla
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? 'Creazione…' : 'Crea cliente'}
            </Button>
          </div>
        </form>
      </Dialog>

      {openClientId && (
        <ClientDetailDrawer clientId={openClientId} onClose={() => setOpenClientId(null)} />
      )}
    </AppLayout>
  )
}
